import { z } from 'zod';

/**
 * Shared `addToOfferId` search-param field for routes that can be entered
 * while "adding units to offer X" (the catalog's own `addToOfferId`, see
 * `features/media-units/lib/validate-search.ts`). Used by every OTHER route
 * that forwards this context through a round trip: `/map` and
 * `/media-units/add` (CU-86caypxaw).
 *
 * Preprocess coerces a bare numeric query value (e.g. `?addToOfferId=999`
 * parses to a JSON number) to its canonical string form instead of dropping
 * it, matching validate-search.ts's `validateMediaUnitsSearch` numeric-coercion
 * branch — a numerically-typed id must survive the round trip through this
 * field, not just a string-typed one. Any other non-string, non-finite-number
 * value is dropped (treated as absent, not a route crash) instead of throwing
 * a SearchParamError. An empty string is also dropped, matching
 * validateMediaUnitsSearch's truthy check — `?addToOfferId=` is absent, not a
 * context for offer id `''`.
 *
 * The outer `.optional()` (not just the inner one) is required: Zod's static
 * `z.input<>` marks a `z.preprocess()` field as required regardless of the
 * inner schema's optionality — only wrapping the whole preprocess call in
 * `.optional()` makes the object key optional for TS callers like
 * `navigate({ search: {...} })`.
 */
export const addToOfferIdSearchField = z
  .preprocess((val) => {
    if (typeof val === 'string' && val) return val;
    if (typeof val === 'number' && Number.isFinite(val)) return String(val);
    return undefined;
  }, z.string().optional())
  .optional();
